import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import TCProductSelectStore, {
    AccessoryType,
    DeviceType,
    IndoorDevice,
    PriceResponse,
    ProductSelectActions,
    RemoteDevice,
} from '@/components/productselect/tc.product.select.store';
import { useStore, waitForStore } from '@tk/utilities/tk.use.store';
import TCDeviceQuantityCounter from './tc.device.quantity.counter';
import TCProductSelect from './tc.product.select';
import { fetchRequest } from '@tk/utilities/tk.fetch';

export default class TCRemoteDevices extends TKCustomElementFactory {
    store?: TCProductSelectStore;
    protected unsubscribe?: () => void;
    contextDeviceId: string | null;

    remoteDeviceAttribute = 'data-tc-device="remote"';
    articleNumberAttribute = 'data-tc-artno';
    imgAttribute = 'data-tc-device-img';
    titleAttribute = 'data-tc-device-title';
    subTitleAttribute = 'data-tc-device-subtitle';
    quantityAttribute = 'data-tc-device-quantity';
    priceAttribute = 'data-tc-device-price';
    uvpAttribute = 'data-tc-device-price-uvp';
    deviceTypeAttribute = 'data-tc-device-type';
    remoteIndoorDeviceIdAttribute = 'data-tc-indoor-device-id';
    inclusiveAttribute = 'data-tc-device-inclusive';
    activeAttribute = 'data-tc-device-active';
    activateButtonAttribute = 'data-tc-active-button';

    remoteTemplateSelector = '[data-tc-remote-template]';
    remoteWrapperSelector = '[data-tc-remote-wrapper]';
    remoteWrapper?: HTMLElement;
    remoteTemplate?: HTMLTemplateElement;

    productSelectTagName = 'tc-product-select';
    productSelect?: TCProductSelect;
    waitForPrices: boolean = false;

    constructor() {
        super();
        this.contextDeviceId = this.getAttribute('data-tc-context-device');
        this.remoteTemplate = document.querySelector(this.remoteTemplateSelector) || undefined;
        this.remoteWrapper = this.querySelector(this.remoteWrapperSelector) || undefined;
        this.productSelect = document.querySelector(this.productSelectTagName) as TCProductSelect;
    }

    async connectedCallback() {
        if (!this.contextDeviceId) throw new Error('Missing context device id!');
        await waitForStore('productSelectStore');
        this.store = useStore('productSelectStore') as TCProductSelectStore;
        this.initializeStore();
    }

    disconnectedCallback(): void {
        this.unsubscribe && this.unsubscribe();
    }

    initializeStore() {
        if (!this.store) return;
        this.unsubscribe = this.store.subscribe(({ action }) => this.render(action as ProductSelectActions));
        this.sendInitialDataToStore();
        this.productSelect?.asyncPrices && this.store?.getLoginStatus() && this.fetchPrices();
        this.registerClickListener();
    }

    sendInitialDataToStore() {
        const contextDevice: IndoorDevice | undefined = this.store?.getDevice(this.contextDeviceId!) as IndoorDevice;
        if (contextDevice.deviceType !== DeviceType.INDOOR) return;
        if (contextDevice.remotesInitalized === true) {
            this.generateRemotes();
            return;
        }

        const remoteElements = this.querySelectorAll(`[${this.remoteDeviceAttribute}]`);
        const devices: RemoteDevice[] = [];
        if (remoteElements.length === 0) return;

        remoteElements.forEach((element) => {
            const remoteDevice: RemoteDevice = {
                id: element.id,
                artNo: element.getAttribute(this.articleNumberAttribute) || '',
                title: element.getAttribute(this.titleAttribute) || '',
                subtitle: element.getAttribute(this.subTitleAttribute) || undefined,
                price: element.getAttribute(this.priceAttribute) || '',
                uvpPrice: element.getAttribute(this.uvpAttribute) || '',
                image: element.getAttribute(this.imgAttribute) || undefined,
                isActive: element.getAttribute(this.activeAttribute) === '1',
                inclusive: element?.getAttribute(this.inclusiveAttribute) === '1',
                parentId: this.contextDeviceId!,
                deviceType: DeviceType.REMOTE,
                hasSiblings: true,
            };

            devices.push(remoteDevice);
        });

        this.store?.setRemoteDevices(this.contextDeviceId!, devices);
    }

    registerClickListener() {
        this.pushListener({
            event: 'click',
            element: this,
            action: this.handleClick.bind(this),
        });
    }

    handleClick(event: Event) {
        const target = event.target as HTMLElement;

        if (target.closest(`[${this.activateButtonAttribute}]`)) {
            const deviceElement = target.closest(`[${this.remoteDeviceAttribute}]`);
            const id = deviceElement?.id;
            if (!this.contextDeviceId || !id) return;
            this.store?.setActiveRemoteDevice(this.contextDeviceId, id);
        }
    }

    render(action: ProductSelectActions) {
        if (this.waitForPrices && action === ProductSelectActions.SET_ACCESSORY_PRICES) this.renderPrices();
        if (action !== ProductSelectActions.SET_ACTIVE_REMOTE) return;
        this.generateRemotes();
    }

    generateRemotes() {
        const remotes = this.store?.getRemoteDevices(this.contextDeviceId!);
        if (remotes?.length === 0 || !this.remoteTemplate || !this.remoteWrapper) return;
        this.remoteWrapper.innerHTML = '';
        remotes!.forEach((remote) =>
            TCRemoteDevices.renderRemote(
                remote,
                this.remoteTemplate!,
                this.remoteWrapper!,
                1,
                !!this.store?.getLoginStatus(),
            ),
        );
    }

    static renderRemote(
        remote: RemoteDevice,
        template: HTMLTemplateElement,
        wrapper: HTMLElement,
        quantity = 1,
        loginStatus: boolean,
    ) {
        const fragment = template.content.cloneNode(true) as DocumentFragment;
        const remoteElement = fragment.firstElementChild as HTMLElement;
        if (!remoteElement) return;

        remoteElement.id = remote.id;

        const img = remoteElement.querySelector('[data-tc-device-img]') as HTMLImageElement;
        if (img && remote.image) {
            img.src = remote.image;
        }

        const title = remoteElement.querySelector('[data-tc-device-title]');
        if (title) title.textContent = remote.title;

        const subtitle = remoteElement.querySelector('[data-tc-device-subtitle]');
        if (subtitle) subtitle.innerHTML = remote.subtitle || '';

        if (remote.isActive) remoteElement.classList.add('remote--active');
        if (remote.inclusive) remoteElement.classList.add('remote--inclusive');
        if (remote.parentId) {
            const changeButton = remoteElement.querySelector('[data-tc-change-remote-button]') as HTMLElement;
            changeButton && (changeButton.dataset.tkUrl += remote.parentId);
        }

        if (!loginStatus) wrapper.appendChild(remoteElement);
        else {
            const price = remoteElement.querySelector('[data-tc-device-price]');
            const priceContent = TCDeviceQuantityCounter.calculateTotalPrice(remote.price, quantity);
            if (priceContent !== '-') {
                price && (price.textContent = priceContent);
            } else {
                const priceWrapper = price?.closest('[data-tc-device-price-wrapper]') as HTMLElement;
                priceWrapper && priceWrapper.setAttribute('hidden', 'true');
            }
            wrapper.appendChild(remoteElement);
        }
    }

    fetchPrices() {
        this.waitForPrices = true;
        const ids = this.store?.getRemoteIds(this.contextDeviceId!);
        if (!ids || !this.productSelect?.priceUrl) return;
        const payload = {
            ids,
        };

        fetchRequest({
            requestURL: this.productSelect.priceUrl,
            resolveHandler: this.updatePrices.bind(this),
            payload,
        });
    }

    updatePrices(response: TKResponse<PriceResponse>) {
        if (!response.success || !response.dataAsJson) return;
        this.store?.setAccessoryPrices(AccessoryType.REMOTE, this.contextDeviceId!, response.dataAsJson);
    }

    renderPrices() {
        this.waitForPrices = false;
        this.generateRemotes();
    }
}
